import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, HelpCircle, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";

function Item({ q, a, list, index, isOpen, onToggle }) {
    return (
        <div
            className={`border bg-white rounded-2xl transition-colors ${
                isOpen ? "border-[#00aeef]/40" : "border-neutral-200 hover:border-neutral-300"
            }`}
            data-testid={`faq-item-${index}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-start justify-between gap-4 sm:gap-6 text-left p-5 sm:p-6 lg:p-8"
                aria-expanded={isOpen}
                data-testid={`faq-toggle-${index}`}
            >
                <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                    <span
                        className={`font-mono text-xs mt-1 ${
                            isOpen ? "text-[#0077b6]" : "text-neutral-500"
                        }`}
                    >
                        {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3
                        className={`font-display text-base sm:text-lg lg:text-xl font-bold tracking-tight transition-colors ${
                            isOpen ? "text-neutral-900" : "text-neutral-800"
                        }`}
                    >
                        {q}
                    </h3>
                </div>
                <span
                    className={`flex-shrink-0 mt-1 w-8 h-8 sm:w-9 sm:h-9 rounded-full border flex items-center justify-center transition-colors ${
                        isOpen
                            ? "border-[#00aeef] bg-[#00aeef]/[0.06] text-[#0077b6]"
                            : "border-neutral-300 text-neutral-600"
                    }`}
                >
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </span>
            </button>

            {isOpen && (
                <div className="px-5 sm:px-6 lg:px-8 pb-6 sm:pb-7 pl-[2.75rem] sm:pl-[3.6rem] lg:pl-[4.5rem]">
                    {a.map((p) => (
                        <p key={p.slice(0, 32)} className="text-neutral-600 leading-relaxed mb-3">
                            {p}
                        </p>
                    ))}
                    {list && (
                        <ul className="mt-3 space-y-2">
                            {list.map((li) => (
                                <li key={li.slice(0, 32)} className="flex gap-3 text-neutral-700">
                                    <span className="text-[#0077b6] mt-1">·</span>
                                    <span className="leading-relaxed">{li}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

export default function FAQ() {
    const { t } = useTranslation();
    // First item open by default; clicking another swaps focus.
    const [openIndex, setOpenIndex] = useState(0);
    const items = t("faq.items", { returnObjects: true }) || [];

    return (
        <div data-testid="faq-page" className="max-w-[1000px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24">
            <div className="border-b border-neutral-200 pb-8 sm:pb-12 mb-8 sm:mb-12">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3 flex items-center gap-2">
                    <HelpCircle className="w-3 h-3" /> {t("faq.eyebrow")}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold uppercase tracking-tighter">
                    {t("faq.titleLine1")}
                    <br />
                    <span className="text-[#0077b6]">{t("faq.titleLine2")}</span>
                </h1>
                <p className="mt-5 sm:mt-6 text-neutral-600 max-w-2xl leading-relaxed text-sm sm:text-base">
                    {t("faq.intro")}
                </p>
            </div>

            <div className="space-y-4">
                {items.map((item, i) => (
                    <Item
                        key={item.q}
                        q={item.q}
                        a={item.a}
                        list={item.list}
                        index={i}
                        isOpen={openIndex === i}
                        onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                    />
                ))}
            </div>

            {/* Still have questions */}
            <div className="mt-16 border border-[#00aeef]/30 rounded-2xl p-8 lg:p-12 relative overflow-hidden">
                <div className="absolute inset-0  opacity-20 pointer-events-none" />
                <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">
                            {t("faq.stillEyebrow")}
                        </div>
                        <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
                            {t("faq.stillTitle")}
                        </h2>
                        <p className="text-neutral-600 mt-2 max-w-lg leading-relaxed">
                            {t("faq.stillBody")}
                        </p>
                    </div>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-3 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 transition-colors whitespace-nowrap"
                        data-testid="faq-contact-cta"
                    >
                        {t("faq.contactUs")} <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
